import { createAnalytics } from '@primer-sdk-web/analytics/analytics';
import { getAPIVersion } from '@primer-sdk-web/core/getAPIVersion';
import { CacheConfig } from '@primer-sdk-web/utils/cacheRequest';

import type { ClientConfiguration } from '../models/ClientConfiguration';
import { retryableGet } from '../utils/http';

import { preloadStateProcessor } from '../payment-methods/backend-driven/preloadStateProcessor';

import { decodeClientToken } from './decodeClientToken';
import { getPrimerHeaders } from './getPrimerHeaders';

export async function fetchClientConfiguration(
  clientToken: string,
  checkoutSessionId: string,
  cacheConfig?: CacheConfig,
) {
  const decodedClientToken = decodeClientToken(clientToken);
  const {
    accessToken,
    configurationUrl,
    analyticsUrlV2: analyticsUrl,
  } = decodedClientToken;

  // Alongside the configuration request.
  preloadStateProcessor(decodedClientToken);

  const analytics = createAnalytics({ checkoutSessionId, url: analyticsUrl });

  const apiVersionFlag = getAPIVersion();
  const apiVersion = apiVersionFlag;

  analytics.timerStart({ id: 'CONFIGURATION_LOADING' });

  try {
    const { data, source } = await retryableGet<ClientConfiguration>(
      `${configurationUrl}/?withDisplayMetadata=true`,
      {
        checkoutSessionId,
        headers: getPrimerHeaders(accessToken, checkoutSessionId, apiVersion),
        retryConfig: {
          enabled: true,
        },
        cacheConfig,
      },
    );

    analytics.timerEnd({
      id: 'CONFIGURATION_LOADING',
      context: { source },
    });

    return data;
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error occurred';

    throw new Error(`Failed to initialize client: ${errorMessage}`);
  }
}
